import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Patient } from "@/types";
import { format } from 'date-fns';

interface PatientPaymentFormProps {
  patientId: string;
  onPaymentUpdate: (amount: number, paymentMode: string) => void;
  onClose: () => void;
  patient: Patient;
}

export function PatientPaymentForm({ 
  patientId, 
  onPaymentUpdate, 
  onClose,
  patient
}: PatientPaymentFormProps) {
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const paymentAmount = Number(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid payment amount.",
        variant: "destructive",
      });
      return;
    }

    // Calculate total cost with proper null checks
    const servicesCost = patient.services?.reduce((total, service) => {
      const price = Number(service.price) || 0;
      return total + price;
    }, 0) || 0;
    // Remove consultationFee (not present in Patient type)
    const totalCost = servicesCost;
    const amountPaid = Number(patient.amountPaid) || 0;
    const remainingAmount = totalCost - amountPaid;

    if (paymentAmount > remainingAmount) {
      toast({
        title: "Invalid Amount",
        description: `Payment amount cannot exceed the remaining amount of ₹${remainingAmount.toFixed(2)}`,
        variant: "destructive",
      });
      return;
    }

    onPaymentUpdate(paymentAmount, paymentMode);
    setAmount("");
    setPaymentMode("cash");
    
    // Show success message
    toast({
      title: "Payment recorded",
      description: `₹${paymentAmount.toFixed(2)} payment recorded successfully on ${format(new Date(), 'dd MMM yyyy, HH:mm')}`,
    });
    
    // Close the dialog immediately (no delay)
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="amount">Payment Amount</Label>
        <Input
          id="amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Enter payment amount"
          min="0"
          step="0.01"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="paymentMode">Payment Mode</Label>
        <Select value={paymentMode} onValueChange={setPaymentMode}>
          <SelectTrigger>
            <SelectValue placeholder="Select payment mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="upi">UPI</SelectItem>
            <SelectItem value="insurance">Insurance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" className="w-full">
          Payment Received
        </Button>
      </div>
    </form>
  );
}
